import { useState, useEffect, useRef } from 'react';
import { Zap, Heart, Store, Clock, MapPin, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Perk {
  id: string;
  title: string;
  merchant: string;
  discount: number;
  location: string;
  isFlashDeal: boolean;
  flashDealEndsAt?: string;
  availableQuantity?: number;
  imageUrl: string | null;
  imageAlt: string;
}

interface FlashDealCardProps {
  deal: Perk;
  onDealClick: () => void;
  onMerchantClick: () => void;
  isWishlisted: boolean;
  onToggleWishlist: () => void;
}

const FlashDealCard = ({ deal, onDealClick, onMerchantClick, isWishlisted, onToggleWishlist }: FlashDealCardProps) => {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endTime = new Date(deal.flashDealEndsAt || '');
      const difference = endTime.getTime() - now.getTime();

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft('Expired');
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [deal.flashDealEndsAt]);

  const percentageLeft = ((deal.availableQuantity || 0) / 200) * 100; // Assuming 200 is max quantity

  return (
    <div className="flex-shrink-0 w-80 bg-white rounded-lg shadow-sm border-2 border-red-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer">
      {/* Image Section */}
      <div className="relative h-40 bg-gradient-to-br from-red-100 to-orange-100 overflow-hidden">
        {deal.imageUrl ? (
          <img
            src={deal.imageUrl}
            alt={deal.imageAlt || `${deal.title} flash deal promotional image`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Zap className="w-12 h-12 text-red-500/30" />
          </div>
        )}

        {/* Flash Deal Badge */}
        <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1.5 rounded-full font-bold text-xs animate-pulse flex items-center">
          <Zap className="w-3.5 h-3.5 mr-1" />
          FLASH DEAL
        </div>

        {/* Wishlist Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist();
          }}
          className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          <Heart
            className={`w-4 h-4 ${isWishlisted ? 'text-red-500 fill-current' : 'text-gray-600'}`}
          />
        </button>

        {/* Discount Badge */}
        <div className="absolute bottom-3 right-3 bg-red-600 text-white px-4 py-1.5 rounded-full font-bold text-lg shadow-lg">
          {deal.discount}% OFF
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4" onClick={onDealClick}>
        {/* Merchant */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onMerchantClick();
          }}
          className="flex items-center text-sm text-gray-600 hover:text-primary mb-2 transition-colors duration-150"
        >
          <Store className="w-3.5 h-3.5 mr-1" />
          {deal.merchant}
        </button>

        {/* Title */}
        <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2">
          {deal.title}
        </h3>

        {/* Countdown Timer */}
        <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg mb-3">
          <div className="flex items-center text-red-600">
            <Clock className="w-4 h-4 mr-2" />
            <span className="text-sm font-semibold">Ends in:</span>
          </div>
          <span className="text-sm font-bold text-red-600 tabular-nums">
            {timeLeft}
          </span>
        </div>

        {/* Availability Progress */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>{t('Available')}</span>
            <span className="font-semibold">{deal.availableQuantity} left</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                percentageLeft > 50 ? 'bg-green-500' : percentageLeft > 25 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
              style={{ width: `${percentageLeft}%` }}
            />
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center text-xs text-gray-500 mb-3">
          <MapPin className="w-3 h-3 mr-1" />
          {deal.location}
        </div>

        {/* Action Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDealClick();
          }}
          className="w-full px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
        >
          <ShoppingCart className="w-4 h-4 mr-2" />
          Grab This Deal
        </button>
      </div>
    </div>
  );
};

interface FlashDealsSectionProps {
  deals: Perk[];
  onDealClick: (deal: Perk) => void;
  onMerchantClick: (merchant: string) => void;
  wishlist: string[];
  onToggleWishlist: (perkId: string) => void;
}

const FlashDealsSection = ({ deals, onDealClick, onMerchantClick, wishlist, onToggleWishlist }: FlashDealsSectionProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(
        container.scrollLeft < container.scrollWidth - container.clientWidth
      );
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 350;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Zap className="w-6 h-6 text-red-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">Flash Deals</h2>
          <span className="ml-3 px-2 py-1 bg-red-100 text-red-600 text-xs font-semibold rounded-full">
            Limited Time
          </span>
        </div>
      </div>

      <div className="relative">
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 -ml-4 hover:bg-gray-50 transition-colors duration-150"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
        )}

        {/* Deals Container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {deals.map((deal) => (
            <FlashDealCard
              key={deal.id}
              deal={deal}
              onDealClick={() => onDealClick(deal)}
              onMerchantClick={() => onMerchantClick(deal.merchant)}
              isWishlisted={wishlist.includes(deal.id)}
              onToggleWishlist={() => onToggleWishlist(deal.id)}
            />
          ))}
        </div>

        {/* Right Scroll Button */}
        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 -mr-4 hover:bg-gray-50 transition-colors duration-150"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>
        )}
      </div>
    </div>
  );
};

export default FlashDealsSection;
